import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { auth } from '../../lib/firebase';

/**
 * Firebase Auth session state, shared by every onboarding step and by the
 * post-onboarding app shell. `orgId` is read from the `orgId` custom claim
 * on the current ID token — never trust any other source for it (CLAUDE.md:
 * "orgId da sessão sempre vem do custom claim").
 */
interface AuthContextValue {
  /** `undefined` while Firebase hasn't resolved the initial auth state yet, `null` once resolved with no session. */
  user: User | null | undefined;
  /** `orgId` custom claim on the current ID token, or `null` if the user has none yet (hasn't finished onboarding's CNPJ step). */
  orgId: string | null;
  /**
   * Forces a fresh ID token fetch (`getIdToken(true)`) and re-reads the
   * `orgId` claim from it — required right after `POST
   * /internal/orgs/{orgId}/select` sets the claim server-side, since the
   * client's cached token predates that write (see CnpjStep.tsx).
   */
  refreshOrgClaim: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [orgId, setOrgId] = useState<string | null>(null);

  useEffect(() => {
    return onAuthStateChanged(auth, async (nextUser) => {
      setUser(nextUser);
      if (!nextUser) {
        setOrgId(null);
        return;
      }
      const tokenResult = await nextUser.getIdTokenResult();
      const claim = tokenResult.claims['orgId'];
      setOrgId(typeof claim === 'string' && claim.length > 0 ? claim : null);
    });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      orgId,
      refreshOrgClaim: async () => {
        const current = auth.currentUser;
        if (!current) {
          setOrgId(null);
          return null;
        }
        const tokenResult = await current.getIdTokenResult(true);
        const claim = tokenResult.claims['orgId'];
        const nextOrgId = typeof claim === 'string' && claim.length > 0 ? claim : null;
        setOrgId(nextOrgId);
        return nextOrgId;
      },
    }),
    [user, orgId],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
